import type { AuthChangeEvent, Session, User } from "@supabase/supabase-js";
import { analytics } from "@/services/analytics";
import { supabase } from "@/integrations/supabase/client";
import { devLog } from "@/utils/devLog";
import { listMfaFactorsTimed } from "@/utils/supabasePasswordSignIn";
import { authService } from "@/services/auth";
import {
  formatAttemptsRemainingMessage,
  getAttemptsRemaining,
  shouldWarnBeforeLockout,
} from "@/utils/loginLockout";
import { hasPersistedSupabaseSession } from "@/utils/authSessionGrace";

const ACCOUNT_LOCKED_CODE = "ACCOUNT_LOCKED";

export interface PasswordSignInResult {
  error: Error | null;
  mfaRequired?: boolean;
  accountLocked?: boolean;
  lockoutCheckUnavailable?: boolean;
  failedAttemptsMessage?: string | null;
  accessToken?: string | null;
}

function createAccountLockedError(): Error {
  const error = new Error(
    "Account temporarily locked due to too many failed login attempts. Try again in 15 minutes."
  );
  (error as Error & { code: string }).code = ACCOUNT_LOCKED_CODE;
  return error;
}

function createLockoutCheckUnavailableError(): Error {
  const error = new Error(
    "Unable to verify account security status. Please try again in a moment."
  );
  (error as Error & { code: string }).code = "LOCKOUT_CHECK_UNAVAILABLE";
  return error;
}

export function hasOAuthTokenInHash(): boolean {
  const hash = globalThis.location?.hash ?? "";
  return hash.includes("access_token") || hash.includes("refresh_token");
}

export function hasOAuthAccessTokenInHash(): boolean {
  return (globalThis.location?.hash ?? "").includes("access_token");
}

/**
 * Maybe Set Just Logged In Flag.
 */
export function maybeSetJustLoggedInFlag(
  wasLoggedOut: boolean,
  source: string,
  options?: { requireRedirectCheck?: boolean }
): void {
  const currentPath = globalThis.location.pathname;
  const flagAlreadySet = sessionStorage.getItem("just_logged_in") === "true";
  const isRedirecting = sessionStorage.getItem("redirecting_to_dashboard") === "true";
  const onAuthRoute = currentPath === "/blog" || currentPath.startsWith("/auth");
  const redirectOk = options?.requireRedirectCheck ? !isRedirecting : true;

  if (wasLoggedOut && !flagAlreadySet && redirectOk && onAuthRoute) {
    sessionStorage.setItem("just_logged_in", "true");
    devLog.warn(`✅ AuthContext: Set just_logged_in flag for ${source}`, {
      wasLoggedOut,
      currentPath,
      flagWasSet: flagAlreadySet,
      isRedirecting,
    });
  }
}

/**
 * Track OAuth Login.
 */
export function trackOAuthLogin(session: Session): void {
  const provider = session.user?.app_metadata?.provider;
  if (!hasOAuthAccessTokenInHash() || !provider) {
    return;
  }

  devLog.warn(`✅ OAuth login complete: ${provider}`, {
    email: session.user.email,
  });
  analytics.trackAuthEvent("login", {
    email: session.user.email,
    method: provider,
    isOAuth: true,
  });
}

interface SignedInMfaCallbacks {
  setUser: (user: User) => void;
  setSession: (session: Session) => void;
  setLoading: (loading: boolean) => void;
  setMfaChallenge: (value: boolean) => void;
  setMfaFactorId: (value: string | null) => void;
  setMfaChallengeId: (value: string | null) => void;
}

export function completeSignedInSession(
  newSession: Session,
  wasLoggedOut: boolean,
  callbacks: SignedInMfaCallbacks
): void {
  trackOAuthLogin(newSession);
  maybeSetJustLoggedInFlag(wasLoggedOut, "SIGNED_IN redirect");
  callbacks.setSession(newSession);
  callbacks.setUser(newSession.user);
  callbacks.setLoading(false);
}

/**
 * Run Signed In Mfa Check.
 */
export async function runSignedInMfaCheck(
  newSession: Session,
  wasLoggedOut: boolean,
  isMounted: () => boolean,
  callbacks: SignedInMfaCallbacks,
  onComplete: () => void
): Promise<void> {
  if (!isMounted()) {
    devLog.warn("⚠️ Component unmounted, skipping MFA check");
    return;
  }

  try {
    devLog.warn("🔍 Listing MFA factors (after delay)...");
    const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
    devLog.warn("🔍 Factors result:", { factors, error: factorsError });

    if (factorsError) {
      devLog.warn("❌ Factors error:", factorsError);
      completeSignedInSession(newSession, wasLoggedOut, callbacks);
      onComplete();
      return;
    }

    const totpFactor = factors?.totp?.find((factor) => factor.status === "verified");
    devLog.warn("🔍 Verified TOTP factor:", totpFactor);

    if (totpFactor) {
      devLog.warn("🔒 VERIFIED TOTP FOUND - Creating challenge...");
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: totpFactor.id,
      });

      devLog.warn("🔍 Challenge result:", { challenge, error: challengeError });

      if (!challengeError && challenge) {
        devLog.warn("✅✅✅ MFA CHALLENGE CREATED - SHOWING PROMPT ✅✅✅");
        callbacks.setUser(newSession.user);
        callbacks.setMfaChallenge(true);
        callbacks.setMfaFactorId(totpFactor.id);
        callbacks.setMfaChallengeId(challenge.id);
        callbacks.setLoading(false);
        onComplete();
        return;
      }

      devLog.warn("❌ Challenge creation failed:", challengeError);
    } else {
      devLog.warn("ℹ️ No verified TOTP factor found - proceeding without MFA");
    }

    devLog.warn("✅ Setting session - no MFA needed");
    completeSignedInSession(newSession, wasLoggedOut, callbacks);
    onComplete();
  } catch (error) {
    devLog.warn("❌ MFA check exception:", error);
    completeSignedInSession(newSession, wasLoggedOut, callbacks);
    onComplete();
  }
}

export interface AuthStateChangeContext {
  mounted: boolean;
  user: User | null;
  session: Session | null;
  loading: boolean;
  mfaChallenge: boolean;
  mfaFactorId: string | null;
  mfaChallengeId: string | null;
  mfaCheckInProgress: boolean;
  manualPasswordSignIn: boolean;
  sessionRestorePending: boolean;
}

export interface AuthStateChangeActions {
  setSession: (session: Session | null) => void;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setMfaChallenge: (value: boolean) => void;
  setMfaFactorId: (value: string | null) => void;
  setMfaChallengeId: (value: string | null) => void;
  setMfaCheckInProgress: (value: boolean) => void;
  setSessionRestorePending: (value: boolean) => void;
}

function handleSignedOut(actions: AuthStateChangeActions): void {
  devLog.warn("🔧 Handling sign out - clearing all auth state");
  actions.setSession(null);
  actions.setUser(null);
  actions.setMfaChallenge(false);
  actions.setMfaFactorId(null);
  actions.setMfaChallengeId(null);
  actions.setMfaCheckInProgress(false);
  devLog.warn("✅ Session cleared");
  actions.setLoading(false);
}

function scheduleSignedInMfaCheck(
  newSession: Session,
  wasLoggedOut: boolean,
  isMounted: () => boolean,
  callbacks: SignedInMfaCallbacks,
  onComplete: () => void
): void {
  setTimeout(() => {
    void runSignedInMfaCheck(newSession, wasLoggedOut, isMounted, callbacks, onComplete);
  }, 100);
}

/**
 * Handle Signed In Event.
 */
function handleSignedInEvent(
  newSession: Session,
  context: AuthStateChangeContext,
  actions: AuthStateChangeActions,
  isMounted: () => boolean
): void {
  const isOAuthCallback = hasOAuthAccessTokenInHash();
  const provider = newSession.user?.app_metadata?.provider;

  if (isOAuthCallback && provider) {
    devLog.warn(`🔵 OAuth SIGNED_IN event: ${provider}`, {
      email: newSession.user.email,
      hasHash: isOAuthCallback,
    });
  }

  actions.setMfaCheckInProgress(true);
  scheduleSignedInMfaCheck(
    newSession,
    !context.user,
    isMounted,
    {
      setUser: actions.setUser,
      setSession: actions.setSession,
      setLoading: actions.setLoading,
      setMfaChallenge: actions.setMfaChallenge,
      setMfaFactorId: actions.setMfaFactorId,
      setMfaChallengeId: actions.setMfaChallengeId,
    },
    () => actions.setMfaCheckInProgress(false)
  );
}

/**
 * Handle Initial Or Refreshed Session.
 */
function handleInitialOrRefreshedSession(
  event: AuthChangeEvent,
  newSession: Session,
  context: AuthStateChangeContext,
  actions: AuthStateChangeActions
): void {
  const isOAuthCallback = hasOAuthAccessTokenInHash();
  const provider = newSession.user?.app_metadata?.provider;

  if (isOAuthCallback && provider) {
    devLog.warn(`🔵 OAuth callback processed: ${provider}`, {
      event,
      email: newSession.user.email,
    });
    trackOAuthLogin(newSession);
  }

  if (event === "INITIAL_SESSION" && !context.user) {
    maybeSetJustLoggedInFlag(true, "INITIAL_SESSION redirect", {
      requireRedirectCheck: true,
    });
  }

  actions.setSessionRestorePending(false);
  actions.setSession(newSession);
  actions.setUser(newSession.user);
  actions.setLoading(false);
}

/**
 * Handle Mfa Challenge Verified.
 */
function handleMfaChallengeVerified(
  newSession: Session,
  context: AuthStateChangeContext,
  actions: AuthStateChangeActions
): void {
  maybeSetJustLoggedInFlag(!context.user, "MFA verification", { requireRedirectCheck: true });
  actions.setSession(newSession);
  actions.setUser(newSession.user);
  actions.setMfaChallenge(false);
  actions.setMfaFactorId(null);
  actions.setMfaChallengeId(null);
  actions.setLoading(false);
  actions.setMfaCheckInProgress(false);
}

/**
 * Process Auth State Change Event.
 */
export function processAuthStateChangeEvent(
  event: AuthChangeEvent,
  newSession: Session | null,
  context: AuthStateChangeContext,
  actions: AuthStateChangeActions,
  isMounted: () => boolean
): void {
  const hasOAuthHash = hasOAuthTokenInHash();

  if (hasOAuthHash && (event === "SIGNED_IN" || event === "INITIAL_SESSION")) {
    devLog.warn(`🔵 OAuth callback detected: ${event}`, {
      provider: newSession?.user?.app_metadata?.provider,
      email: newSession?.user?.email,
      hasHash: hasOAuthHash,
    });
  }

  if (!isMounted()) return;

  if (!newSession) {
    if (
      event === "INITIAL_SESSION" &&
      (context.sessionRestorePending || hasPersistedSupabaseSession())
    ) {
      devLog.warn("INITIAL_SESSION null while session restore pending — deferring sign-out");
      return;
    }
    handleSignedOut(actions);
    return;
  }

  if (
    event === "SIGNED_IN" &&
    !context.mfaCheckInProgress &&
    !context.manualPasswordSignIn &&
    !context.session
  ) {
    handleSignedInEvent(newSession, context, actions, isMounted);
    return;
  }

  if (event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED") {
    handleInitialOrRefreshedSession(event, newSession, context, actions);
    return;
  }

  if (event === "MFA_CHALLENGE_VERIFIED") {
    handleMfaChallengeVerified(newSession, context, actions);
    return;
  }

  actions.setLoading(false);
}

/**
 * Finalize Password Sign In.
 */
export async function finalizePasswordSignIn(
  session: Session,
  email: string,
  accessToken: string | null,
  callbacks: SignedInMfaCallbacks
): Promise<{
  error: Error | null;
  mfaRequired?: boolean;
  accessToken?: string | null;
}> {
  devLog.warn("🔍 Checking for MFA factors...");
  const { data: factors, error: factorsError } = await listMfaFactorsTimed();

  if (factorsError) {
    devLog.warn("❌ MFA factors error:", factorsError);
    return { error: factorsError, accessToken };
  }

  const totpFactor = factors?.totp?.find((factor) => factor.status === "verified");
  if (totpFactor) {
    devLog.warn("🔒 MFA required - creating challenge...");
    const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
      factorId: totpFactor.id,
    });

    if (!challengeError && challengeData) {
      callbacks.setMfaChallenge(true);
      callbacks.setMfaFactorId(totpFactor.id);
      callbacks.setMfaChallengeId(challengeData.id);
      return { error: null, mfaRequired: true, accessToken };
    }

    devLog.warn("❌ MFA challenge error:", challengeError);
    return { error: challengeError ?? new Error("MFA challenge failed"), accessToken };
  }

  callbacks.setSession(session);
  callbacks.setUser(session.user);
  analytics.trackAuthEvent("login", { email });
  return { error: null, accessToken };
}

/**
 * Validate Sign In Lockout.
 */
export async function validateSignInLockout(
  normalizedEmail: string
): Promise<PasswordSignInResult | null> {
  const lockoutStatus = await authService.checkAccountLockout(normalizedEmail);
  if (lockoutStatus.checkUnavailable) {
    return {
      error: createLockoutCheckUnavailableError(),
      lockoutCheckUnavailable: true,
    };
  }
  if (lockoutStatus.locked) {
    return { error: createAccountLockedError(), accountLocked: true };
  }
  return null;
}

/**
 * Warn If Approaching Lockout.
 */
export function warnIfApproachingLockout(
  lockoutStatus: Awaited<ReturnType<typeof authService.checkAccountLockout>>,
  onLockoutWarning?: (message: string) => void
): void {
  if (!shouldWarnBeforeLockout(lockoutStatus)) {
    return;
  }
  const warning = formatAttemptsRemainingMessage(getAttemptsRemaining(lockoutStatus));
  if (warning) {
    onLockoutWarning?.(warning);
  }
}

/**
 * Handle Failed Password Sign In.
 */
export async function handleFailedPasswordSignIn(
  normalizedEmail: string,
  error: Error
): Promise<PasswordSignInResult> {
  await authService.logLoginAttempt({
    email: normalizedEmail,
    success: false,
    failureReason: error.message,
  });

  const postFailLockout = await authService.checkAccountLockout(normalizedEmail);
  const failedAttemptsMessage = postFailLockout.checkUnavailable
    ? null
    : formatAttemptsRemainingMessage(getAttemptsRemaining(postFailLockout));

  if (!postFailLockout.checkUnavailable && postFailLockout.locked) {
    return {
      error: createAccountLockedError(),
      accountLocked: true,
      failedAttemptsMessage,
    };
  }

  return { error, failedAttemptsMessage };
}
